import { useEffect, useRef } from 'react';
import MedicalHistoryChart from '../MedicalHistoryChart';
import { FormProvider, useForm } from 'react-hook-form';
import { TMedicalHistoryChartFormValues } from 'types/chart/medicalHistoryChart';
import { MedicalHistoryChartFormDefaultValues } from 'constants/chart/medicalHistoryChart';
import useChartRegister from 'hooks/chart/useChartRegister';
import { TChartInfoResponse } from 'hooks/chart/useGetChartInfo';
import {
  TEvaluationChartInfo,
  TEvaluationChartInfoResponse,
  TPutEvaluationChartResponse,
} from 'types/apis/chart/studentEvaluationChart';
import useModal from 'hooks/useModal';
import ConfirmChartRegister from 'pages/modal/ConfirmChartRegister';
import ChartBottomBtn from 'components/pages/chart/ChartBottomBtn';
import { TPracticeChartInfo } from 'types/apis/chart/studentPracticeChart';

type TProps = {
  query: URLSearchParams;
  chartType: number;
  category: string;
  id: number;
  chartInfoResponse: TChartInfoResponse | null;
  setChartInfoResponse: (data: any) => void;
  activeTabIndex: number;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  isRegister: boolean;
  isEdit: boolean;
  setSubmitEndDate: (submitEndDate: string) => void;
  isReadOnly: boolean;
  checkSubmitEndDate: (submitEndDate: string) => void;
  handleSuccessGetChartInfo: (data: TChartInfoResponse) => void;
};

const MedicalHistoryChartRegister = ({
  query,
  chartType,
  category,
  id,
  chartInfoResponse,
  setChartInfoResponse,
  activeTabIndex,
  isLoading,
  setIsLoading,
  isRegister,
  isEdit,
  setSubmitEndDate,
  isReadOnly,
  checkSubmitEndDate,
  handleSuccessGetChartInfo,
}: TProps) => {
  const { openModal } = useModal();

  const { chartRegister } = useChartRegister(({ data }) => afterAutoSave(data));

  const methods = useForm<TMedicalHistoryChartFormValues>({
    defaultValues: MedicalHistoryChartFormDefaultValues,
  });
  const { handleSubmit, setValue, watch, reset } = methods;
  const formValues = watch();
  const formValuesRef = useRef(formValues);
  const prevFormValuesRef = useRef<TMedicalHistoryChartFormValues | null>(null);

  useEffect(() => {
    if (isLoading) return;
    prevFormValuesRef.current = formValues;
  }, [isLoading]);

  useEffect(() => {
    formValuesRef.current = formValues;
  }, [formValues]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (!isLoading && isRegister && category === 'evaluation') {
      intervalId = setInterval(() => {
        if (isReadOnly) return;
        // 초기 상태 자동 저장 방지
        const chartInfoList = chartInfoResponse?.chartInfo as
          | TEvaluationChartInfo[]
          | undefined;
        if (
          chartInfoList &&
          chartInfoList.length > (activeTabIndex > 1 ? 1 : 0)
        ) {
          const currentChartInfo = chartInfoList[activeTabIndex > 1 ? 1 : 0];
          if (
            currentChartInfo?.chartData === null &&
            JSON.stringify(MedicalHistoryChartFormDefaultValues) ===
              JSON.stringify(formValuesRef.current)
          ) {
            return;
          }
        }

        // 이전 값과 비교하여 변경된 경우만 자동 저장 요청
        if (
          JSON.stringify(formValuesRef.current) !==
          JSON.stringify(prevFormValuesRef.current)
        ) {
          onValid(true, formValuesRef.current);
        }
      }, 20000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRegister, isLoading, category, isReadOnly]);

  const afterAutoSave = ({ chartInfo }: TPutEvaluationChartResponse) => {
    setChartInfoResponse((prev: TEvaluationChartInfoResponse) => {
      if (!prev) return prev;
      const newChartInfo = prev.chartInfo as TEvaluationChartInfo[];
      const newChartInfoData = [...newChartInfo];
      newChartInfoData[activeTabIndex > 1 ? 1 : 0] = {
        ...newChartInfoData[activeTabIndex > 1 ? 1 : 0],
        chartData: chartInfo?.chartData || null,
        fileInfoList: chartInfo?.fileInfoList || [],
      };
      return {
        ...prev,
        chartInfo: newChartInfoData,
      };
    });
    prevFormValuesRef.current = chartInfo?.chartData || null;
  };

  const setChartInfo = (
    chartInfo: TPracticeChartInfo | TEvaluationChartInfo
  ) => {
    const chartData: TMedicalHistoryChartFormValues | null =
      (chartInfo?.chartData as TMedicalHistoryChartFormValues | null) || null;

    if (category !== 'practice') {
      const submitEndDate =
        (chartInfo as TEvaluationChartInfo)?.submitEndDate || '';
      setSubmitEndDate(submitEndDate);
      checkSubmitEndDate(submitEndDate);
    }
    if (!chartData) {
      reset();
      setIsLoading(false);
      return;
    }
    Object.keys(chartData).forEach((key) => {
      setValue(
        key as keyof TMedicalHistoryChartFormValues,
        chartData[key as keyof TMedicalHistoryChartFormValues]
      );
    });

    setIsLoading(false);
  };

  useEffect(() => {
    if (chartInfoResponse) {
      handleSuccessGetChartInfo(chartInfoResponse);
    }
  }, []);

  // 차트 조회
  useEffect(() => {
    if (!chartInfoResponse) return;
    const chartInfo =
      category === 'practice'
        ? (chartInfoResponse.chartInfo as TPracticeChartInfo)
        : (chartInfoResponse.chartInfo as TEvaluationChartInfo[])[
            activeTabIndex > 1 ? 1 : 0
          ];
    setIsLoading(true);
    setChartInfo(chartInfo);
  }, [activeTabIndex, chartInfoResponse]);

  // 차트 등록
  const onValid = (
    isAutoSave: boolean,
    data: TMedicalHistoryChartFormValues
  ) => {
    const formData = new FormData();

    formData.append(
      'request',
      new Blob(
        [
          JSON.stringify({
            chartType,
            chartData: data,
          }),
        ],
        {
          type: 'application/json',
        }
      )
    );
    chartRegister({
      category,
      id: id,
      data: formData,
      isEdit: isEdit,
      isAutoSave,
      patientId: query.get('patientId') || undefined,
      courseInfoId: query.get('courseInfoId') || undefined,
      chartRound: query.get('chartRound') || undefined,
      onLoading: setIsLoading,
      onConfirm: () => {
        openModal({
          element: (
            <ConfirmChartRegister
              category={category}
              chartType={chartType}
              id={id}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              chartName={chartInfoResponse?.chartName}
              data={formData}
            />
          ),
        });
      },
    });
  };

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit((data) => onValid(false, data))}>
          <MedicalHistoryChart
            isRegister={isRegister}
            isReadOnly={isReadOnly}
          />
          {(Number(query.get('tab')) === 0 ||
            Number(query.get('tab')) === 2) && (
            <ChartBottomBtn
              category={category}
              id={id}
              courseId={query.get('courseId') || undefined}
              patientId={query.get('patientId') || undefined}
              courseInfoId={query.get('courseInfoId') || undefined}
              chartRound={query.get('chartRound') || undefined}
              studentUserId={query.get('studentUserId') || undefined}
              isRegister={isRegister}
              isEdit={isEdit}
              isReadOnly={isReadOnly}
              isChart={true}
              chartName={chartInfoResponse?.chartName}
            />
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default MedicalHistoryChartRegister;
